import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IServiceResponse,
  IServiceReq,
  IAddOrUpdateService,
  ICommonListPayloadDto,
} from "../type/type.ts";
import axios from "axios";
import { RootState } from "../store/store.ts";
import Axios from "../axios-config/axiosInstance.ts";
import { IService } from "../type/type.ts";

interface ServiceMasterState {
  services: IService[];
  loading: boolean;
  error: string | null;
  serviceTypeSelect: {
    label: string;
    value: number;
  }[];
  offset: number;
  limit: number;
  sortField: string;
  sortOrder: number;
  dataCount: number;
  searchInput: string;
}

const initialState: ServiceMasterState = {
  services: [],
  loading: false,
  error: null,
  dataCount: 0,
  offset: 0,
  limit: 10,
  sortField: "createdOn",
  sortOrder: 1,
  serviceTypeSelect: [],
  searchInput: ""
};

export const fetchServices = createAsyncThunk<IServiceResponse, ICommonListPayloadDto>(
  'services/fetch',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await Axios.get<IServiceResponse>(`service`, { params: payload });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch services");
    }
  }
);


export const addService = createAsyncThunk<
  IAddOrUpdateService,
  IServiceReq,
  { rejectValue: string }
>("service/add", async (values, { rejectWithValue }) => {
  try {
    const response = await Axios.post<IAddOrUpdateService>(
      "service",
      values
    );
    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to add service"
    );
  }
});

//  update service
export const updateService = createAsyncThunk<
  IAddOrUpdateService,
  Partial<IService> & { id: number; name: string },
  { rejectValue: string }
>("service/update", async (value, { rejectWithValue }) => {
  try {
    const response = await Axios.put<IAddOrUpdateService>(`service/${value.id}`, { name: value.name });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || "Failed to update service name");
  }
}
);

// delete service
export const deleteService = createAsyncThunk<
  number, // Return type (deleted ID)
  number, // Input type (ID to delete)
  { rejectValue: string }
>("service/delete", async (id, { rejectWithValue }) => {
  try {
    await Axios.delete(`service/${id}`);
    return id;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Failed to delete service"
    );
  }
});

export const updateStatus = createAsyncThunk<IAddOrUpdateService, { id: number; isActive: boolean }, { rejectValue: string }>(
  "service/updateStatus ",
  async ({ id, isActive }, { rejectWithValue }) => {
    try {
      const response = await Axios.put<IAddOrUpdateService>(`service/${id}`, { isActive });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to update service status");
    }
  }
);



// Create Slice
const ServiceMasterSlice = createSlice({
  name: "serviceMaster",
  initialState,
  reducers: {
    setOffset: (state, action) => {
      state.offset = action.payload;
    },
    setLimit: (state, action) => {
      state.limit = action.payload;
    },
    setSortField: (state, action) => {
      state.sortField = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    setSearchInput: (state, action) => {
      state.searchInput = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action: PayloadAction<IServiceResponse>) => {
        state.loading = false;
        state.services = action.payload.data.data || [];
        state.dataCount = action.payload.data.total;
      })

      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || "Failed to load services";
      })
      .addCase(addService.pending, (state) => {
        state.loading = true;
      })
      .addCase(addService.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.services = [...state.services, action.payload.data];
      })

      .addCase(addService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to add service";
      })

      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        if (!action.payload?.data) {
          console.error("Update payload missing data:", action.payload);
          return;
        }

        const index = state.services.findIndex(
          (cat) => cat.id === action.payload.data.id
        );
        if (index !== -1) {
          state.services[index] = action.payload.data;
        }

        state.loading = false;
        state.error = null;
      })

      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update service";
      })

      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        if (typeof action.payload !== "number") {
          console.error("Delete failed, invalid payload:", action.payload);
          return;
        }

        state.services = state.services.filter(
          (cat) => cat.id !== action.payload
        );
        state.loading = false;
      })

      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete service";
      })

      .addCase(updateStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(updateStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })

      .addCase(updateStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update service status";
      });
  },
});

export const { setOffset, setLimit, setSortField, setSortOrder, setSearchInput } = ServiceMasterSlice.actions;
export default ServiceMasterSlice.reducer;
